// Die Leiste oben (Ersatz für das schwebende Telefon-Feld): zwischen Brotkrumen-Zeile und Seiteninhalt, volle Breite,
// weiß, darunter die blaue Linie (Close: Power Dialer / aktiver Anruf). Links Richtung (OUT), Kontakt und Firma,
// rechts „Anruf Lead 4 von 3.600“, Tastenfeld, Pille mit Kürzel ▾ (Stumm, Aufs Handy, Pause, Sitzung beenden,
// Mikrofon und Lautsprecher mit Pegel), roter Auflegen-Knopf, blauer „Nächster Anruf →“.
// Einheitliche Anrufleiste (18.10.2026): JEDER Anruf (einzeln aus Lead/Liste/Inbox, Rückruf, eingehend angenommen, Nummer
// wählen, Power Dialer) steht in dieser einen Leiste: Name oder Nummer, Status und Dauer, Stumm, Tastenfeld, Aufs Handy,
// Aufnahme-Zeichen, Geräte, rotes Auflegen. Beim Power Dialer kommen Lead x/y, Countdown, Pause, Nächster, Verlassen dazu.
// Die Leiste steht auf jeder Seite, solange ein Gespräch oder eine Power-Dialer-Sitzung läuft. Einzelanrufe haben
// dieselbe Leiste ohne „Lead x von y“ und ohne „Nächster Anruf“. Kein Fenster, kein Pflichtformular.
// Design-Runde 2 (Close-Ablauf): eine kompakte Zeile. Nach dem Auflegen läuft sichtbar ein Countdown (Standard 10 s,
// `countdown.ts`), dann wird automatisch der nächste Lead gewählt; Tippen oder ein offenes Formular halten ihn an,
// nach 5 s Ruhe läuft er weiter. „Pause“ hält den Dialer an, „Verlassen“ beendet die Sitzung und blendet die Leiste aus.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { modTaste, tipptGerade } from "../werkzeuge/tasten";
import { telefonText } from "../werkzeuge/telefon";
import { useAuth } from "../auth/AuthContext";
import { Ausklapp } from "../components/Ausklapp";
import { Icon } from "../components/Icon";
import { AnrufenKnopf } from "./AnrufenMenue";
import { EingehendLeiste } from "./EingehendKarte";
import { useDialerCountdown } from "./countdown";
import { mikrofonHilfeBeiSperre } from "./desktop";
import { dauerText, dispositionText, kuerzel, pegelAnteil, tastenZeichen, warnungKurz, zahlText } from "./logik";
import { setzeMikrofonDb, useMikrofonDb, useMikrofonTest } from "./pegel";
import { tel } from "./api";
import { Symbol } from "./Symbole";
import { useTelefonie } from "./TelefonieProvider";
import "./telefonie.css";

const ZUSTAND_TEXT: Record<string, string> = {
  nicht_eingerichtet: "nicht eingerichtet",
  initialisiert: "wird geladen",
  bereit: "bereit",
  verbindet: "verbindet …",
  klingelt: "klingelt …",
  im_gespraech: "im Gespräch",
  beendet: "beendet",
  fehler: "Fehler",
};

const TASTEN = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

function useSekundenSeit(iso: string | null | undefined): number {
  const [jetzt, setJetzt] = useState(Date.now());
  useEffect(() => {
    if (!iso) return;
    const t = window.setInterval(() => setJetzt(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [iso]);
  return iso ? Math.max(0, Math.floor((jetzt - new Date(iso).getTime()) / 1000)) : 0;
}

// ---------------------------------------------------------------------------
// Tastenfeld
// ---------------------------------------------------------------------------

function Tastenfeld({ aktiv, offen, setOffen }: { aktiv: boolean; offen: boolean; setOffen: (o: boolean) => void }) {
  const t = useTelefonie();
  const tRef = useRef(t);
  tRef.current = t;
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [getippt, setGetippt] = useState("");
  const [gedrueckt, setGedrueckt] = useState<string | null>(null);
  const callId = t.aktiverAnruf?.callId ?? null;
  useEffect(() => {
    if (!aktiv) setOffen(false);
  }, [aktiv, setOffen]);
  // Neues Gespräch: leeres Tastenfeld
  useEffect(() => {
    setGetippt("");
  }, [callId]);

  const senden = useCallback((z: string) => {
    setGetippt((g) => (g + z).slice(-20));
    setGedrueckt(z);
    window.setTimeout(() => setGedrueckt((x) => (x === z ? null : x)), 160);
    void tRef.current.tasten(z);
  }, []);

  // Bei offenem Tastenfeld senden auch die Tasten der Tastatur (0 bis 9, *, #), außer man tippt gerade in ein Feld
  // (Notiz). Capture-Phase: Eingaben im offenen Feld selbst erreichen das Fenster sonst nicht.
  useEffect(() => {
    if (!offen || !aktiv) return;
    const taste = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing || tipptGerade(e)) return;
      const z = tastenZeichen(e.key);
      if (!z) return;
      e.preventDefault();
      senden(z);
    };
    window.addEventListener("keydown", taste, true);
    return () => window.removeEventListener("keydown", taste, true);
  }, [offen, aktiv, senden]);

  return (
    <>
      <button
        ref={setAnker}
        type="button"
        className={`tel-leistenknopf${offen ? " tel-leistenknopf--an" : ""}`}
        title={`Tastenfeld: Ziffern senden, auch mit der Tastatur (${modTaste}⇧D)`}
        aria-label="Tastenfeld"
        aria-expanded={offen}
        disabled={!aktiv}
        data-tel-tastenfeld=""
        onClick={() => setOffen(!offen)}
      >
        <Symbol name="tasten" groesse={16} />
        <span className="tel-leistenknopf__text">Tastenfeld</span>
      </button>
      <Ausklapp anker={anker} offen={offen && aktiv} onSchliessen={() => setOffen(false)} ausrichtung="end" breite={212} rolle="dialog">
        <div className="tel-tastenfeld tel-tastenfeld--pop" data-tel-tastenfeld-feld="">
          <div className="tel-getippt" data-tel-getippt="" aria-live="polite" aria-label="Gesendete Ziffern">
            {getippt || " "}
          </div>
          {TASTEN.map((z) => (
            <button key={z} type="button" className={`tel-taste${gedrueckt === z ? " tel-taste--an" : ""}`} aria-label={`Taste ${z}`} onClick={() => senden(z)}>
              {z}
            </button>
          ))}
        </div>
      </Ausklapp>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pille mit Kürzel ▾
// ---------------------------------------------------------------------------

function Pegelbalken({ db }: { db: number | null }) {
  const anteil = db === null ? 0 : pegelAnteil(db);
  const leise = db !== null && db < -50;
  return (
    <div className="tel-pegel tel-pegel--breit" aria-label="Mikrofonpegel" title={db === null ? "nicht gemessen" : `${db} dBFS`}>
      <div className={`tel-pegel__fuellung${leise ? " tel-pegel__fuellung--leise" : ""}`} style={{ width: `${Math.round(anteil * 100)}%` }} />
    </div>
  );
}

function Geraete({ offen }: { offen: boolean }) {
  const t = useTelefonie();
  const imGespraech = !!t.aktiverAnruf;
  const [eingaenge, setEingaenge] = useState<MediaDeviceInfo[]>([]);
  const [ausgaenge, setAusgaenge] = useState<MediaDeviceInfo[]>([]);
  const [eingang, setEingang] = useState("");
  const [ausgang, setAusgang] = useState("");
  const [freigabe, setFreigabe] = useState(true);
  const [meldung, setMeldung] = useState<string | null>(null);
  const laufDb = useMikrofonDb();
  const test = useMikrofonTest(offen && !imGespraech, eingang || null);

  const laden = useCallback(async () => {
    try {
      const liste = await navigator.mediaDevices.enumerateDevices();
      setEingaenge(liste.filter((g) => g.kind === "audioinput" && g.deviceId !== "default"));
      setAusgaenge(liste.filter((g) => g.kind === "audiooutput" && g.deviceId !== "default"));
      setFreigabe(liste.some((g) => g.kind === "audioinput" && g.label));
    } catch {
      setFreigabe(false);
    }
    try {
      const e = await tel.einstellungenLesen();
      setEingang(e.input_device_id ?? "");
      setAusgang(e.output_device_id ?? "");
    } catch {
      /* Standardgeräte */
    }
  }, []);

  useEffect(() => {
    if (!offen) return;
    void laden();
    navigator.mediaDevices?.addEventListener?.("devicechange", laden);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", laden);
  }, [offen, laden]);

  const waehlen = async (neuEin: string, neuAus: string) => {
    setMeldung(null);
    setEingang(neuEin);
    setAusgang(neuAus);
    try {
      await t.geraeteSetzen(neuEin || null, neuAus || null);
    } catch (e) {
      setMeldung((e as Error).message || "Gerät lässt sich nicht wechseln.");
    }
  };

  const db = imGespraech ? laufDb : test.db;
  const leise = imGespraech ? t.warnungen.some((w) => w.name === "mikrofon-leise") : test.urteil === "leise" || test.urteil === "stille";
  return (
    <div className="tel-geraete">
      <div className="menue__ueberschrift">Mikrofon</div>
      {!freigabe && (
        <button
          type="button"
          className="tel-linkknopf"
          onClick={() =>
            void navigator.mediaDevices
              .getUserMedia({ audio: true })
              .then((s) => (s.getTracks().forEach((x) => x.stop()), laden()))
              .catch((e) => {
                mikrofonHilfeBeiSperre(e);
                setMeldung("Mikrofon nicht freigegeben.");
              })
          }
        >
          Mikrofon freigeben (für die Gerätenamen)
        </button>
      )}
      <select className="eingabe" value={eingang} aria-label="Mikrofon" onChange={(e) => void waehlen(e.target.value, ausgang)}>
        <option value="">Standard des Systems</option>
        {eingaenge.map((g) => (
          <option key={g.deviceId} value={g.deviceId}>
            {g.label || "Mikrofon"}
          </option>
        ))}
      </select>
      <Pegelbalken db={db} />
      <p className={leise ? "tel-geraete__hinweis tel-geraete__hinweis--leise" : "tel-klein"}>
        {test.fehler && !imGespraech
          ? test.fehler
          : leise
            ? `Mikrofon zu leise (unter −50 dBFS${db !== null ? `, jetzt ${db}` : ""}). Anderes Gerät wählen oder das Headset prüfen.`
            : db === null
              ? imGespraech
                ? "Pegel wird gemessen …"
                : "Sprich kurz „Hallo“: der Balken sollte deutlich ausschlagen."
              : `Pegel ${db} dBFS${imGespraech ? "" : " · sprich kurz „Hallo“, Sprechen liegt bei −20 bis −30"}`}
      </p>
      <div className="menue__ueberschrift">Lautsprecher</div>
      <select className="eingabe" value={ausgang} aria-label="Lautsprecher" onChange={(e) => void waehlen(eingang, e.target.value)}>
        <option value="">Standard des Systems</option>
        {ausgaenge.map((g) => (
          <option key={g.deviceId} value={g.deviceId}>
            {g.label || "Lautsprecher"}
          </option>
        ))}
      </select>
      {meldung && <p className="tel-klein">{meldung}</p>}
    </div>
  );
}

function Pille({ offen, setOffen }: { offen: boolean; setOffen: (o: boolean) => void }) {
  const t = useTelefonie();
  const { nutzer } = useAuth();
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const a = t.aktiverAnruf;
  const d = t.dialer;
  const aufHandy = a?.handy === "verbunden" || a?.serverZustand === "auf_handy";
  const handyLaeuft = a?.handy === "wird_angerufen" || a?.handy === "klingelt";
  const leise = t.warnungen.some((w) => w.schwere !== "info");
  const zu = () => setOffen(false);
  const punkt = (text: string, klick: () => void, extra?: { aus?: boolean; hinweis?: string; gefahr?: boolean; trenner?: boolean }) => (
    <button
      type="button"
      role="menuitem"
      className={`menue__punkt${extra?.gefahr ? " menue__punkt--gefahr" : ""}${extra?.trenner ? " menue__trenner" : ""}`}
      disabled={extra?.aus}
      title={extra?.hinweis}
      onClick={() => {
        zu();
        klick();
      }}
    >
      <span className="menue__text">{text}</span>
    </button>
  );
  return (
    <>
      <button
        ref={setAnker}
        type="button"
        className={`tel-pille${leise ? " tel-pille--warnt" : ""}`}
        title="Mikrofon, Lautsprecher und weitere Optionen"
        aria-haspopup="menu"
        aria-expanded={offen}
        onClick={() => setOffen(!offen)}
      >
        <Symbol name="telefon" groesse={14} />
        <span className="tel-pille__kuerzel">{kuerzel(nutzer?.name)}</span>
        {leise ? <span className="tel-punkt tel-punkt--gelb" aria-hidden="true" /> : null}
        <Icon name="chevron-unten" groesse={12} />
      </button>
      <Ausklapp anker={anker} offen={offen} onSchliessen={zu} ausrichtung="end" breite={320} maxHoehe={560} rolle="menu">
        <div className="menue tel-pillenmenue">
          {t.warnungen
            .filter((w) => w.schwere !== "info")
            .map((w) => (
              <div key={w.name} className="tel-klein tel-pillenmenue__hinweis tel-pillenmenue__warnung" data-tel-warnung-text="">
                {w.text}
                {w.handy && a && !a.direkt && !aufHandy && !handyLaeuft ? (
                  <button type="button" className="tel-linkknopf" onClick={() => { zu(); void t.aufsHandy(); }}>
                    Aufs Handy holen
                  </button>
                ) : null}
              </div>
            ))}
          {a && !aufHandy
            ? punkt(a.stumm ? "Stumm aus" : "Stumm", () => t.stumm(!a.stumm), { aus: !!a.leitungWeg })
            : null}
          {a && !a.direkt && !aufHandy
            ? punkt(handyLaeuft ? "Handy klingelt …" : "Aufs Handy", () => void t.aufsHandy(), {
                aus: !a.callId || handyLaeuft || t.status === "verbindet",
                hinweis: t.info?.phone ? undefined : "Keine Handynummer hinterlegt (Einstellungen > Telefonie)",
              })
            : null}
          {a?.direkt ? <div className="tel-klein tel-pillenmenue__hinweis">Direkt verbunden: kein „Aufs Handy“.</div> : null}
          {d ? punkt(d.pausiert ? "Pause aufheben" : "Pause", () => t.dialerPause(!d.pausiert)) : null}
          {d ? punkt("Sitzung beenden", () => void t.dialerBeenden(), { gefahr: true }) : null}
          {!a && !d ? <div className="tel-klein tel-pillenmenue__hinweis">Kein Anruf aktiv.</div> : null}
          {t.info?.caller_id ? <div className="tel-klein tel-pillenmenue__hinweis">Von {telefonText(t.info.caller_id)} (Auswahl: Headset oben rechts)</div> : null}
          <div className="tel-pillenmenue__trenner" />
          <Geraete offen={offen} />
        </div>
      </Ausklapp>
    </>
  );
}

// ---------------------------------------------------------------------------
// Die Leiste
// ---------------------------------------------------------------------------

function LeistenInhalt() {
  const t = useTelefonie();
  const tRef = useRef(t);
  tRef.current = t;
  const ort = useLocation();
  const [pilleOffen, setPilleOffen] = useState(false);
  const [tastenOffen, setTastenOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const a = t.aktiverAnruf;
  const n = t.nacharbeit;
  const d = t.dialer;
  const sekunden = useSekundenSeit(a?.angenommenUm ?? null);
  const cd = useDialerCountdown();

  const leadId = a?.leadId ?? n?.leadId ?? d?.aktuell?.lead?.id ?? null;
  const imLead = !!n && ort.pathname === `/lead/${encodeURIComponent(n.leadId)}`;

  // Ein beendeter Einzelanruf bleibt nur auf der Lead-Seite stehen; wer weggeht, schließt ihn damit.
  const beendetSchliessen = t.beendetSchliessen;
  const verwaist = !a && !!n && !d && !imLead;
  useEffect(() => {
    if (verwaist) beendetSchliessen();
  }, [verwaist, beendetSchliessen]);

  // Kürzel: ⌘⇧S Stumm, ⌘⇧D Tastenfeld (im Gespräch). Wirken auch beim Tippen, nicht unter offenen Dialogen.
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || !(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      const k = e.key.toLowerCase();
      const x = tRef.current;
      const anruf = x.aktiverAnruf;
      if (!anruf) return;
      const amHandy = anruf.handy === "verbunden" || anruf.serverZustand === "auf_handy";
      if (k === "s" && !anruf.leitungWeg && !amHandy) {
        e.preventDefault();
        x.stumm(!anruf.stumm);
      } else if (k === "d" && x.status === "im_gespraech" && !amHandy) {
        e.preventDefault();
        setTastenOffen((o) => !o);
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, []);

  const zeigen = !!a || (!!n && (!!d || imLead)) || !!d || t.preflight.laeuft || t.panelOffen;
  if (!zeigen) return null;

  const person = a?.kontaktName ?? n?.kontaktName ?? d?.aktuell?.contact?.name ?? null;
  const firmaRoh = a?.leadName ?? n?.leadName ?? d?.aktuell?.lead?.name ?? null;
  // Firma = Kontakt (Einzelunternehmer, Lead trägt den Namen der Person): nur einmal zeigen
  const gleich = (x: string | null, y: string | null) => !!x && !!y && x.trim().toLowerCase() === y.trim().toLowerCase();
  const firma = gleich(person, firmaRoh) ? null : firmaRoh;
  const nummerRoh = a?.nummer ?? n?.call.remote_number ?? d?.aktuell?.nummer ?? null;
  const nummer = nummerRoh ? telefonText(nummerRoh) : null;
  const aufHandy = !!a && (a.handy === "verbunden" || a.serverZustand === "auf_handy");
  const handyLaeuft = a?.handy === "wird_angerufen" || a?.handy === "klingelt";
  const warn = t.warnungen.filter((w) => w.schwere !== "info");
  const leerlauf = !a && !n && !d; // nur geöffnet (Telefon-Knopf in der Seitenleiste) oder Meldung
  const eingehend = !!a?.eingehend;
  const imGespraech = !!a && t.status === "im_gespraech";

  // Status (Chip) und Gesprächsdauer (Zahl) getrennt: „klingelt …“ / „im Gespräch“ und daneben 0:42
  let status = "";
  let stand = "";
  if (a) {
    status = aufHandy ? "auf dem Handy" : (ZUSTAND_TEXT[t.status] ?? "");
    if (imGespraech) stand = dauerText(sekunden);
  } else if (n) {
    const angenommen = !!n.call.answered_at;
    // Ein Wort pro Zustand: nicht erreicht → „Nicht erreicht“ (nicht zusätzlich „beendet“); Gespräch → „beendet“ + Dauer
    if (angenommen) {
      status = "beendet";
      stand = `Gespräch ${dauerText(n.call.duration)}`;
    } else status = dispositionText(n.call.disposition ?? "no-answer");
  } else if (t.preflight.laeuft) stand = "Leitungstest läuft …";
  const restSek = cd ? Math.ceil(cd.rest / 1000) : 0;
  const aufnahme = !!a && !!t.info?.aufnahme;

  let zaehler = "";
  if (d) {
    const pos = d.aktuell?.position ?? d.sitzung.position;
    const von = d.sitzung.von ?? d.sitzung.anzahl;
    zaehler = d.pausiert ? `Pausiert · Lead ${zahlText(pos)} von ${zahlText(von)}` : `Lead ${zahlText(pos)} von ${zahlText(von)}`;
  }

  const ende = d?.aktuell?.grund === "ende";
  const weiter = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (ende) await t.dialerBeenden();
      else await t.dialerWeiter();
    } finally {
      setBusy(false);
    }
  };
  const steuerung = !!a || !!d; // Stumm und Tastenfeld stehen bei jedem Anruf und im Power Dialer (zwischen den Anrufen grau)
  const steuerungAn = !!a && !a.leitungWeg && !aufHandy;

  return (
    <section className="tel-leiste" aria-label="Telefon" data-tel-leiste="" data-tel-richtung={eingehend ? "ein" : leerlauf ? "leer" : "aus"}>
      <div className="tel-leiste__zeile">
        <div className="tel-leiste__richtung" title={leerlauf ? "Telefon" : eingehend ? "Eingehender Anruf" : "Ausgehender Anruf"}>
          {leerlauf ? <Symbol name="telefon" groesse={18} /> : <Icon name={eingehend ? "eingehend" : "ausgehend"} groesse={18} />}
          <span>{leerlauf ? "TEL" : eingehend ? "IN" : "OUT"}</span>
        </div>
        <div className="tel-leiste__wer">
          <div className="tel-leiste__namen">
          {person ? (
            <span className="tel-leiste__person">
              <Icon name="person" groesse={13} />
              {person}
            </span>
          ) : null}
          {firma ? (
            leadId ? (
              <Link className="tel-leiste__firma" to={`/lead/${encodeURIComponent(leadId)}`} title="Zum Lead">
                <Icon name="leads" groesse={13} />
                {firma}
              </Link>
            ) : (
              <span className="tel-leiste__firma">
                <Icon name="leads" groesse={13} />
                {firma}
              </span>
            )
          ) : null}
          </div>
          {(person || firma) && (d || nummer) ? (
            <span className="tel-leiste__unter">
              {d ? (
                <Link to={`/smart-views/${encodeURIComponent(d.sitzung.smart_view_id)}`} className="tel-leiste__zurueck" title="Zurück zur Smart View">
                  <Icon name="pfeil-links" groesse={11} />
                  {d.sitzung.smart_view_name}
                </Link>
              ) : null}
              {nummer ? <span className="tel-leiste__nummer">{nummer}</span> : null}
            </span>
          ) : null}
          {!person && !firma ? (
            <span className="tel-leiste__person" title={nummer ? "Anruf ohne Lead" : undefined}>
              {d
                ? `Power Dialer: ${d.sitzung.smart_view_name}`
                : nummer
                  ? nummer
                  : leerlauf
                    ? t.info?.enabled
                      ? `Telefon bereit${t.info.caller_id ? ` · Absender ${t.info.caller_id}` : ""}`
                      : (t.grund ?? "Telefonie nicht eingerichtet")
                    : "Telefon"}
            </span>
          ) : null}
        </div>

        <div className="tel-leiste__rechts">
          {status ? <span className={`tel-leiste__status tel-leiste__status--${imGespraech ? "gespraech" : n && !a ? "ende" : "warte"}`} data-tel-status="">{status}</span> : null}
          {stand ? <span className="tel-leiste__stand">{stand}</span> : null}
          {aufnahme ? (
            <span className="tel-rec" title="Das Gespräch wird aufgenommen" data-tel-aufnahme="">
              <span className="tel-rec__punkt" aria-hidden="true" />
              Aufnahme
            </span>
          ) : null}
          {cd ? (
            <span className={`tel-countdown${cd.haelt ? " tel-countdown--haelt" : ""}`} role="timer" aria-live="off" data-tel-countdown="" title={cd.haelt ? "Angehalten, solange du am Lead arbeitest. Nach 5 Sekunden Ruhe geht es weiter." : "Der nächste Lead wird gleich gewählt. Pause hält an."}>
              <svg className="tel-countdown__ring" width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
                <circle cx="13" cy="13" r="10.5" className="tel-countdown__spur" />
                <circle cx="13" cy="13" r="10.5" className="tel-countdown__fuell" strokeDasharray={66} strokeDashoffset={66 * (1 - cd.rest / cd.gesamt)} />
              </svg>
              <span className="tel-countdown__text">{cd.haelt ? "Angehalten" : `Nächster Anruf in ${restSek} s`}</span>
            </span>
          ) : null}
          {zaehler ? <span className="tel-leiste__zaehler">{zaehler}</span> : null}
          {d && !leerlauf ? (
            <button
              type="button"
              className={`tel-symbolknopf${d.pausiert ? " tel-symbolknopf--an" : ""}`}
              title={d.pausiert ? "Weiter: Countdown und automatisches Wählen laufen wieder" : "Pause: hält Countdown und automatisches Wählen an (⌘.)"}
              aria-label={d.pausiert ? "Weiter" : "Pause"}
              aria-pressed={d.pausiert}
              onClick={() => t.dialerPause(!d.pausiert)}
              data-tel-pause=""
            >
              <Icon name={d.pausiert ? "play" : "pause"} groesse={16} />
            </button>
          ) : null}
          {leerlauf ? <AnrufenKnopf /> : null}
          {steuerung ? (
            <>
              <button
                type="button"
                className={`tel-leistenknopf${a?.stumm ? " tel-leistenknopf--an" : ""}`}
                title={`${a?.stumm ? "Stumm aufheben" : "Stumm schalten"} (${modTaste}⇧S)`}
                aria-label="Stumm"
                aria-pressed={!!a?.stumm}
                disabled={!steuerungAn}
                data-tel-stumm=""
                onClick={() => a && t.stumm(!a.stumm)}
              >
                <Symbol name={a?.stumm ? "mikro-aus" : "mikro"} groesse={16} />
                <span className="tel-leistenknopf__text">Stumm</span>
              </button>
              <Tastenfeld aktiv={imGespraech && !aufHandy} offen={tastenOffen} setOffen={setTastenOffen} />
              {a && !a.direkt && !aufHandy ? (
                <button
                  type="button"
                  className="tel-symbolknopf"
                  title={t.info?.phone ? "Gespräch aufs Handy holen" : "Aufs Handy holen: keine Handynummer hinterlegt (Einstellungen > Telefonie)"}
                  aria-label="Aufs Handy holen"
                  disabled={!a.callId || handyLaeuft || t.status === "verbindet"}
                  data-tel-handy=""
                  onClick={() => void t.aufsHandy()}
                >
                  <Symbol name="handy" groesse={16} />
                </button>
              ) : null}
            </>
          ) : null}
          {warn.length ? (
            <button type="button" className="tel-pillenhinweis" data-tel-warnung="" title={warn.map((w) => w.text).join("\n")} onClick={() => setPilleOffen(true)}>
              <span className={`tel-punkt tel-punkt--gelb`} aria-hidden="true" />
              {warnungKurz(warn[0].name)}
            </button>
          ) : null}
          {!leerlauf || t.info?.enabled ? <Pille offen={pilleOffen} setOffen={setPilleOffen} /> : null}
          {a ? (
            <button type="button" className="tel-auflegen" title="Auflegen (⌘.)" aria-label="Auflegen" onClick={() => void t.auflegen()}>
              <Symbol name="auflegen" groesse={18} />
              <span className="tel-auflegen__text">Auflegen</span>
            </button>
          ) : null}
          {d ? (
            <button type="button" className="tel-naechster" disabled={busy} onClick={() => void weiter()} data-tel-naechster="">
              {ende ? "Sitzung beenden" : "Nächster Anruf"} {ende ? null : <Icon name="pfeil-rechts" groesse={14} />}
            </button>
          ) : null}
          {d ? (
            <button
              type="button"
              className="tel-verlassen"
              title="Verlassen: beendet den Power Dialer und blendet die Leiste aus"
              onClick={() => {
                void t.dialerBeenden();
                if (!a) t.beendetSchliessen();
                t.setPanelOffen(false);
                t.fehlerZuruecksetzen();
              }}
              data-tel-verlassen=""
            >
              <Icon name="schliessen" groesse={13} />
              Verlassen
            </button>
          ) : null}
          {!d && !a && (n || t.panelOffen) ? (
            <button
              type="button"
              className="tel-schliessen"
              title="Schließen"
              aria-label="Leiste schließen"
              onClick={() => {
                t.beendetSchliessen();
                t.setPanelOffen(false);
                t.fehlerZuruecksetzen();
              }}
            >
              <Icon name="schliessen" groesse={14} />
            </button>
          ) : null}
        </div>
      </div>

      {a?.meldung && (
        <div className="tel-hinweis tel-hinweis--info">
          <span className="tel-hinweis__text">{a.meldung}</span>
        </div>
      )}
      {a?.leitungWeg && !aufHandy && !a.direkt && (
        <div className="tel-hinweis tel-hinweis--info">
          <button type="button" className="knopf knopf--klein knopf--primaer" onClick={() => void t.neuVerbinden()}>
            <Symbol name="neu" groesse={14} /> Neu verbinden
          </button>
          <button type="button" className="knopf knopf--klein" onClick={() => void t.aufsHandy()} disabled={handyLaeuft}>
            <Symbol name="handy" groesse={14} /> Aufs Handy holen
          </button>
        </div>
      )}
      {!a && d?.meldung && (
        <div className="tel-hinweis tel-hinweis--info">
          <span className="tel-hinweis__text">{d.meldung}</span>
        </div>
      )}
      {leerlauf && (t.info?.hinweise ?? []).map((h) => (
        <div key={h.code} className="tel-hinweis tel-hinweis--info">
          <span className="tel-hinweis__text">{h.meldung}</span>
        </div>
      ))}
      {leerlauf && !t.info?.enabled && (
        <div className="tel-hinweis tel-hinweis--info">
          <Link className="tel-linkknopf" to="/einstellungen#telefonie">
            Zu den Telefon-Einstellungen
          </Link>
        </div>
      )}
    </section>
  );
}

/** Die Leiste mit festem Platz im App-Rahmen (`components/AppLayout.tsx`): direkt unter der Kopfzeile, immer
 *  eingehängt. Ohne Gespräch, Sitzung, Meldung oder offenes Telefon zeigt sie nichts (der Platz hat dann keine Höhe). */
export function DialerLeiste() {
  const t = useTelefonie();
  // Mikrofonanzeige zurücksetzen, wenn die Leiste verschwindet
  const aktiv = !!t.aktiverAnruf;
  const zuletzt = useRef(aktiv);
  useEffect(() => {
    if (zuletzt.current && !aktiv) setzeMikrofonDb(null);
    zuletzt.current = aktiv;
  }, [aktiv]);
  return (
    <div className="tel-leistenplatz">
      <EingehendLeiste />
      <LeistenInhalt />
      {t.fehler ? (
        <div className="tel-fehlerchip" role="status" aria-live="polite" data-tel-fehler="">
          {t.fehler}
        </div>
      ) : null}
    </div>
  );
}

/** Name aus der Zeit des schwebenden Feldes: der App-Rahmen mountet `DialerPanel` weiter, es ist jetzt die Leiste. */
export const DialerPanel = DialerLeiste;
